import * as Yup from "yup";
import { default as BaseModal } from "@ds-developer/modal";
import { Calendar as CalendarIcon } from "react-feather";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { formatDateShort, formatTime } from "@ds-developer/frontend";
import React, { useEffect, useState } from "react";
import TimePicker from "@ds-developer/time-picker";

import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { TimeSlotInput, TimeSlotsAvailabilityInput } from "@hubcapp/types";
import { ViewLessonModalProps } from "./types";
import { set, startOfDay } from "date-fns";
import ViewLessonModalSkeleton from "./components/Skeleton";

import { AllowedTravelTime } from "@hubcapp/components/Calendar/components/Modals/AddLessonModal/types";
import { ViewLessonModalActions } from "@hubcapp/components/Calendar/components/Modals/ViewLessonModal/ViewLessonModalActions";
import { ViewLessonModalLeanerInfo } from "@hubcapp/components/Calendar/components/Modals/ViewLessonModal/ViewLessonModalLeanerInfo";
import { travelTimeOptions } from "@hubcapp/components/Calendar/components/Modals/constants";
import { validationSchema } from "@hubcapp/components/Calendar/components/Modals/ViewLessonModal/components/validationSchema";
import useQueryGetTimeSlotsAvailability from "@hubcapp/components/Calendar/components/Modals/ViewLessonModal/hooks/useQueryGetTimeSlotsAvailability";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    paddingTop: 0,
  },
  chip: {
    borderRadius: "2px",
    fontWeight: theme.typography.fontWeightRegular,
  },
  link: {
    color: theme.palette.primary.main,
  },
  pickerInput: {
    paddingRight: 0,
  },
  flexGrow: {
    flexGrow: 1,
  },
}));

const ViewLessonModal: React.FC<ViewLessonModalProps> = ({
  loading = false,
  open = false,
  closeViewLessonModal,
  lesson,
  cancelLesson,
  onSubmit,
}) => {
  const classes = useStyles();
  const [formStatus, setFormStatus] = React.useState<"view" | "edit">("view");

  const [
    getTimeSlotsAvailability,
    {
      loading: loadingTimeSlots,
      data: timeSlotsAvailability,
      error: errorTimeSlots,
    },
  ] = useQueryGetTimeSlotsAvailability();

  const defaultErrorValues = {
    startDateTime: null,
    endDateTime: null,
  };
  const [errors, setErrors] = useState<{
    startDateTime: string | null;
    endDateTime: string | null;
  }>(defaultErrorValues);

  const [startDateTime, setStartTime] = useState<MaterialUiPickersDate>(
    lesson?.startDateTime,
  );
  const [endDateTime, setEndTime] = useState<MaterialUiPickersDate>(
    lesson?.endDateTime,
  );
  const [travelTime, setTravelTime] = useState<number>(
    lesson?.travelTimeInMinutes || 0,
  );
  const [test, setTest] = useState<boolean>(Boolean(lesson?.test));

  const [needToCheckAvailability, setCheckAvailability] = useState<boolean>(
    false,
  );

  const setDateHandler = async (
    name: string,
    value: MaterialUiPickersDate,
    setState: React.Dispatch<MaterialUiPickersDate>,
  ) => {
    setState(value);
    const newData = {
      startDateTime: new Date(startDateTime as Date),
      endDateTime: new Date(endDateTime as Date),
      travelTime: +travelTime,
      test,
      [name]: new Date(value as Date),
    };
    if (name === "startDateTime") {
      newData.endDateTime = set(value as Date, {
        hours: new Date(endDateTime as Date).getHours(),
        minutes: new Date(endDateTime as Date).getMinutes(),
      });
      setEndTime(newData.endDateTime);
    }
    try {
      setErrors(defaultErrorValues);
      await validationSchema.validate(newData, { abortEarly: false });
    } catch (e) {
      if (e instanceof Yup.ValidationError) {
        e.inner.forEach(err => {
          if (err.path && errors.hasOwnProperty(err.path)) {
            setErrors(prevState => ({
              ...prevState,
              [err.path as string]: err.message,
            }));
          }
        });
      }
    }
    setCheckAvailability(true);
  };

  const handleTestChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setTest(checked);
  };

  const handleTravelTimeChange = (
    option: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    setTravelTime(Number(option.target.value) as number);
    setCheckAvailability(true);
  };

  const checkAvailability = () => {
    const input = ({
      instructorKey: lesson?.instructor?.id,
      learnerKey: lesson?.learner?.id,
      timeSlots: ([
        {
          startDateTime,
          endDateTime,
          travelTime,
        },
      ] as unknown) as TimeSlotInput[],
      ignoreTimeOff: true,
      ignoreWorkHours: true,
      ignoreCalendarItemIds: [lesson?.id],
    } as unknown) as TimeSlotsAvailabilityInput;
    getTimeSlotsAvailability({
      variables: {
        input,
      },
    });
  };

  useEffect(() => {
    if (needToCheckAvailability) {
      checkAvailability();
      setCheckAvailability(false);
    }
  }, [needToCheckAvailability]);

  let errorMsg = "";
  let errorDisplayStatus = false;

  if (timeSlotsAvailability?.timeSlotsAvailability?.length) {
    errorDisplayStatus = timeSlotsAvailability.timeSlotsAvailability.some(
      availability => availability.availability === false,
    );
    if (errorDisplayStatus) {
      errorMsg =
        "You currently have a calendar item during this time, please cancel it to continue.";
    }
  } else if (errorTimeSlots) {
    errorMsg = "Something went wrong, please try again later.";
  }

  const isTodayOrFutureLesson =
    !!lesson && +new Date(lesson.endDateTime) >= +startOfDay(new Date());

  const actions = (
    <ViewLessonModalActions
      closeViewLessonModal={closeViewLessonModal}
      cancelLesson={cancelLesson}
      onSaveButtonClick={() =>
        onSubmit({
          startDateTime: startDateTime as Date,
          endDateTime: endDateTime as Date,
          travelTime: +travelTime,
          test,
          id: lesson?.id ?? "",
        })
      }
      onEditButtonClick={() => setFormStatus("edit")}
      showEditButton={formStatus === "view" && isTodayOrFutureLesson}
      showSaveButton={formStatus === "edit"}
      disabledSaveButton={loading || loadingTimeSlots || errorDisplayStatus}
      loadingSaveButton={loading || loadingTimeSlots}
    />
  );

  return (
    <BaseModal
      actions={actions}
      open={open}
      title={formStatus === "view" ? "View Lesson" : "Edit Lesson"}
    >
      {loading || !lesson ? (
        <ViewLessonModalSkeleton />
      ) : (
        <Grid container spacing={3} className={classes.root}>
          <ViewLessonModalLeanerInfo lesson={lesson} />

          {formStatus === "view" ? (
            <>
              <Grid item xs={6}>
                <Typography variant="caption">Lesson date</Typography>
                <Typography>
                  {formatDateShort(new Date(lesson.startDateTime))}
                </Typography>
              </Grid>
              <Grid item xs={6} data-test="lesson-test">
                <Typography variant="caption">Test lesson</Typography>
                <Typography data-test="lesson-test-value">
                  {lesson.test ? "Yes" : "No"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">Start time</Typography>
                <Typography>
                  {formatTime(new Date(lesson.startDateTime))}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">End time</Typography>
                <Typography>
                  {formatTime(new Date(lesson.endDateTime))}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">Travel time</Typography>
                <Typography>
                  {
                    travelTimeOptions[
                      (lesson?.travelTimeInMinutes ?? 0) as AllowedTravelTime
                    ]
                  }
                </Typography>
              </Grid>
            </>
          ) : (
            <>
              <Grid container spacing={5}>
                <Grid item xs={12} style={{ paddingBottom: 0 }}>
                  <KeyboardDatePicker
                    id="startDate"
                    onChange={value =>
                      setDateHandler("startDateTime", value, setStartTime)
                    }
                    value={startDateTime}
                    name="startDate"
                    inputVariant="outlined"
                    label="Pick a date"
                    placeholder="dd/mm/yyyy"
                    format="dd/MM/yyyy"
                    fullWidth
                    keyboardIcon={<CalendarIcon />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TimePicker
                    name="startTime"
                    label="Start time"
                    value={startDateTime || undefined}
                    minutesStep={15}
                    helperText={errors.startDateTime ?? ""}
                    isValid={!Boolean(errors.startDateTime)}
                    onChange={(value: Date | null | undefined) => {
                      setDateHandler(
                        "startDateTime",
                        value || null,
                        setStartTime,
                      );
                    }}
                    inputDataTest="lesson-start-time-picker-input"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TimePicker
                    name="endTime"
                    label="End time"
                    value={endDateTime || undefined}
                    minutesStep={15}
                    helperText={errors.endDateTime ?? ""}
                    isValid={!Boolean(errors.endDateTime)}
                    onChange={(value: Date | null | undefined) => {
                      setDateHandler("endDateTime", value || null, setEndTime);
                    }}
                    inputDataTest="lesson-end-time-picker-input"
                  />
                </Grid>

                <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <FormControl variant="outlined" style={{ width: "100%" }}>
                    <InputLabel htmlFor="outlined-age-native-simple">
                      Travel time
                    </InputLabel>
                    <Select
                      data-test="select-travel-time-test"
                      value={travelTime}
                      onChange={handleTravelTimeChange}
                      label="Travel time"
                      inputProps={{
                        name: "travelTime",
                        id: "select-travel-time",
                      }}
                    >
                      {((Object.keys(travelTimeOptions).map(
                        Number,
                      ) as unknown) as AllowedTravelTime[]).map((entry, i) => (
                        <MenuItem key={i} value={entry}>
                          {travelTimeOptions[entry]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} style={{ paddingTop: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={handleTestChange}
                        data-test="lesson-test-checkbox"
                        checked={test}
                      />
                    }
                    label="Is this lesson for a test?"
                  />
                  {errorDisplayStatus && (
                    <Typography color="error">{errorMsg}</Typography>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </BaseModal>
  );
};

export default ViewLessonModal;
